import React, { useState, useRef, useEffect } from 'react';
import Icon from '../../../components/AppIcon';

import { Checkbox } from '../../../components/ui/Checkbox';

const ResultsGrid = ({ 
  results, 
  selectedRows, 
  onRowSelect, 
  onSelectAll, 
  onSort, 
  sortConfig,
  onEdit,
  onDelete,
  onViewAudit
}) => {
  const [expandedRow, setExpandedRow] = useState(null);
  const gridRef = useRef(null);

  const columns = [
    { id: 'tracking', label: 'Tracking Number', sortable: true, width: '200px' },
    { id: 'shipDate', label: 'Ship Date', sortable: true, width: '140px' },
    { id: 'carrier', label: 'Carrier', sortable: true, width: '120px' },
    { id: 'status', label: 'Status', sortable: true, width: '140px' },
    { id: 'user', label: 'Entered By', sortable: true, width: '160px' },
    { id: 'timestamp', label: 'Entry Time', sortable: true, width: '140px' },
    { id: 'actions', label: 'Actions', sortable: false, width: '120px' }
  ];

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'delivered':
        return 'bg-success/10 text-success';
      case 'in transit':
        return 'bg-warning/10 text-warning-foreground';
      case 'pending':
        return 'bg-muted text-muted-foreground';
      case 'exception':
        return 'bg-error/10 text-error';
      default:
        return 'bg-muted text-muted-foreground';
    }
  };

  const getStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case 'delivered':
        return 'CheckCircle2';
      case 'in transit':
        return 'Truck';
      case 'pending':
        return 'Clock';
      case 'exception':
        return 'AlertTriangle';
      default:
        return 'Package';
    }
  };

  const handleSort = (columnId) => {
    if (columns?.find(col => col?.id === columnId)?.sortable) {
      onSort(columnId);
    }
  };

  const handleRowClick = (rowId) => {
    setExpandedRow(expandedRow === rowId ? null : rowId);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date?.toLocaleDateString('en-US', { 
      month: '2-digit', 
      day: '2-digit', 
      year: 'numeric' 
    });
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    return date?.toLocaleTimeString('en-US', { 
      hour: '2-digit', 
      minute: '2-digit',
      hour12: true 
    });
  };

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e?.key === 'Escape') {
        setExpandedRow(null);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  return (
    <div className="bg-card rounded-lg border border-border shadow-elevation-1 overflow-hidden">
      <div className="overflow-x-auto" ref={gridRef}>
        <table className="w-full min-w-[1200px]">
          <thead className="bg-muted border-b border-border">
            <tr>
              <th className="w-[48px] px-18 py-12">
                <Checkbox
                  checked={selectedRows?.length === results?.length && results?.length > 0}
                  indeterminate={selectedRows?.length > 0 && selectedRows?.length < results?.length}
                  onChange={(e) => onSelectAll(e?.target?.checked)}
                  aria-label="Select all rows"
                />
              </th>
              {columns?.map(column => (
                <th
                  key={column?.id}
                  className={`px-18 py-12 text-left ${column?.sortable ? 'cursor-pointer hover:bg-muted/80' : ''}`}
                  style={{ width: column?.width }}
                  onClick={() => column?.sortable && handleSort(column?.id)}
                >
                  <div className="flex items-center gap-8">
                    <span className="font-body font-semibold text-[13px] text-foreground uppercase tracking-wide">
                      {column?.label}
                    </span>
                    {column?.sortable && (
                      <Icon
                        name={
                          sortConfig?.key === column?.id
                            ? sortConfig?.direction === 'asc' ?'ChevronUp' :'ChevronDown' :'ChevronsUpDown'
                        }
                        size={16}
                        color="var(--color-muted-foreground)"
                        strokeWidth={2}
                      />
                    )}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {results?.map((row, index) => (
              <React.Fragment key={row?.id}>
                <tr
                  className={`
                    border-b border-border transition-smooth
                    ${selectedRows?.includes(row?.id) ? 'bg-primary/5' : 'hover:bg-muted/50'}
                    ${expandedRow === row?.id ? 'bg-muted/30' : ''}
                  `}
                >
                  <td className="px-18 py-12">
                    <Checkbox
                      checked={selectedRows?.includes(row?.id)}
                      onChange={() => onRowSelect(row?.id)}
                      aria-label={`Select row ${index + 1}`}
                    />
                  </td>
                  <td className="px-18 py-12">
                    <button
                      onClick={() => handleRowClick(row?.id)}
                      className="font-mono text-[13px] text-primary hover:underline text-left"
                    >
                      {row?.tracking}
                    </button>
                  </td>
                  <td className="px-18 py-12">
                    <span className="font-body text-[14px] text-foreground">
                      {formatDate(row?.shipDate)}
                    </span>
                  </td>
                  <td className="px-18 py-12">
                    <div className="flex items-center gap-8">
                      <div className="w-[32px] h-[32px] rounded-lg bg-primary/10 flex items-center justify-center">
                        <Icon 
                          name="Package" 
                          size={16} 
                          color="var(--color-primary)" 
                          strokeWidth={2} 
                        />
                      </div>
                      <span className="font-body text-[14px] text-foreground font-medium">
                        {row?.carrier}
                      </span>
                    </div>
                  </td>
                  <td className="px-18 py-12">
                    <div className={`inline-flex items-center gap-8 px-12 py-6 rounded-full ${getStatusColor(row?.status)}`}>
                      <Icon 
                        name={getStatusIcon(row?.status)} 
                        size={14} 
                        color="currentColor" 
                        strokeWidth={2} 
                      />
                      <span className="font-caption text-[12px] font-medium">
                        {row?.status}
                      </span>
                    </div>
                  </td>
                  <td className="px-18 py-12">
                    <div className="flex items-center gap-8">
                      <div className="w-[28px] h-[28px] rounded-full bg-primary/20 flex items-center justify-center">
                        <span className="font-caption text-[11px] font-semibold text-primary">
                          {row?.user?.split(' ')?.map(n => n?.[0])?.join('')}
                        </span>
                      </div>
                      <span className="font-body text-[14px] text-foreground">
                        {row?.user}
                      </span>
                    </div>
                  </td>
                  <td className="px-18 py-12">
                    <div className="flex flex-col">
                      <span className="font-body text-[13px] text-foreground">
                        {formatTime(row?.timestamp)}
                      </span>
                      <span className="font-caption text-[11px] text-muted-foreground">
                        {formatDate(row?.timestamp)}
                      </span>
                    </div>
                  </td>
                  <td className="px-18 py-12">
                    <div className="flex items-center gap-6">
                      <button
                        onClick={() => onEdit(row)}
                        className="w-[32px] h-[32px] flex items-center justify-center rounded-lg hover:bg-muted transition-smooth"
                        title="Edit entry"
                        aria-label="Edit entry"
                      >
                        <Icon 
                          name="Edit2" 
                          size={16} 
                          color="var(--color-foreground)" 
                          strokeWidth={2} 
                        />
                      </button>
                      <button
                        onClick={() => onViewAudit(row)}
                        className="w-[32px] h-[32px] flex items-center justify-center rounded-lg hover:bg-muted transition-smooth"
                        title="View audit trail"
                        aria-label="View audit trail"
                      >
                        <Icon 
                          name="History" 
                          size={16} 
                          color="var(--color-foreground)" 
                          strokeWidth={2} 
                        />
                      </button>
                      <button
                        onClick={() => onDelete(row)}
                        className="w-[32px] h-[32px] flex items-center justify-center rounded-lg hover:bg-error/10 transition-smooth"
                        title="Delete entry"
                        aria-label="Delete entry"
                      >
                        <Icon 
                          name="Trash2" 
                          size={16} 
                          color="var(--color-error)" 
                          strokeWidth={2} 
                        />
                      </button>
                    </div>
                  </td>
                </tr>
                {expandedRow === row?.id && (
                  <tr className="bg-muted/30 border-b border-border">
                    <td colSpan={8} className="px-18 py-18">
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-18">
                        <div>
                          <div className="font-caption text-[11px] text-muted-foreground uppercase tracking-wide mb-4">
                            Customer Details
                          </div>
                          <div className="font-body text-[14px] text-foreground">
                            {row?.customerName || 'N/A'}
                          </div>
                        </div>
                        <div>
                          <div className="font-caption text-[11px] text-muted-foreground uppercase tracking-wide mb-4">
                            Destination
                          </div>
                          <div className="font-body text-[14px] text-foreground">
                            {row?.destination || 'N/A'}
                          </div>
                        </div>
                        <div>
                          <div className="font-caption text-[11px] text-muted-foreground uppercase tracking-wide mb-4">
                            Service Type
                          </div>
                          <div className="font-body text-[14px] text-foreground">
                            {row?.serviceType || 'Standard'}
                          </div>
                        </div>
                        <div>
                          <div className="font-caption text-[11px] text-muted-foreground uppercase tracking-wide mb-4">
                            Weight
                          </div>
                          <div className="font-body text-[14px] text-foreground">
                            {row?.weight || 'N/A'}
                          </div>
                        </div>
                        <div>
                          <div className="font-caption text-[11px] text-muted-foreground uppercase tracking-wide mb-4">
                            Cost
                          </div>
                          <div className="font-body text-[14px] text-foreground font-medium">
                            ${row?.cost || '0.00'}
                          </div>
                        </div>
                        <div>
                          <div className="font-caption text-[11px] text-muted-foreground uppercase tracking-wide mb-4">
                            Notes
                          </div>
                          <div className="font-body text-[14px] text-foreground">
                            {row?.notes || 'No notes'}
                          </div>
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
      {results?.length === 0 && (
        <div className="flex flex-col items-center justify-center py-64 px-24">
          <div className="w-[64px] h-[64px] rounded-full bg-muted flex items-center justify-center mb-18">
            <Icon 
              name="Search" 
              size={32} 
              color="var(--color-muted-foreground)" 
              strokeWidth={2} 
            />
          </div>
          <h3 className="font-heading font-semibold text-h5 text-foreground mb-8">
            No Results Found
          </h3>
          <p className="font-body text-[14px] text-muted-foreground text-center max-w-[400px]">
            Try adjusting your search criteria or filters to find what you're looking for.
          </p>
        </div>
      )}
    </div>
  );
};

export default ResultsGrid;